import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { UserCircleIcon } from "@heroicons/react/24/outline";
import { fetchUser } from "../../../api/users";
import { DashboardSetupAlertLink } from "../DashboardSetupAlertLink";
import {
  USER_PROFILE_GUIDE_FIELDS,
  normalizeUserRecord,
  userProfileIssues,
} from "./userSetupStatus";
import { useEmailSignatureNagAudience } from "./useEmailSignatureNagAudience";

/**
 * Forsiden (dashboard): viser et kort til innlogget bruker (ansatt + innleid) når
 * viktige personopplysninger mangler (mobil, pårørende, fødselsnummer, kontonummer).
 * Skjules helt når alt er på plass.
 */
export const DashboardUserSetupAlerts = () => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const { eligible: showEmailSignature } = useEmailSignatureNagAudience();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const res = await fetchUser(currentUser.id, { fields: USER_PROFILE_GUIDE_FIELDS });
        if (cancelled) return;
        const normalized = normalizeUserRecord(res) ?? res;
        const ok = normalized && typeof normalized === "object" && normalized.id != null && !normalized.errors;
        setUser(ok ? normalized : null);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  const issues = useMemo(() => {
    const all = user ? userProfileIssues(user) : [];
    if (showEmailSignature) return all;
    return all.filter((key) => key !== "email_signature");
  }, [user, showEmailSignature]);

  if (loading || issues.length === 0) {
    return null;
  }

  return (
    <DashboardSetupAlertLink
      to="/dashboard/guide/profile#profile-setup-status"
      icon={UserCircleIcon}
      title={t("dashboard.user_guide.alert_profile_title")}
      count={issues.length}
      subtitle={t("dashboard.user_guide.alert_profile_subtitle", { count: issues.length })}
    />
  );
};
